import React, { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { ArrowLeft, Pencil, Plus, X, Save, CalendarOff } from "lucide-react";
import { toast } from "sonner";
import { WEEKDAYS } from "@/config/crm";
import { DoctorFormDialog } from "@/components/doctors/DoctorFormDialog";

export default function DoctorDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [doctor, setDoctor] = useState(null);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editOpen, setEditOpen] = useState(false);
  const [wh, setWh] = useState({});
  const [leaves, setLeaves] = useState([]);
  const [newLeave, setNewLeave] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/doctors/${id}`);
      setDoctor(data);
      setWh(data.working_hours || {});
      setLeaves(data.leaves || []);
    } catch { toast.error("Doctor not found"); navigate("/doctors"); }
    finally { setLoading(false); }
  }, [id, navigate]);

  useEffect(() => { load(); api.get("/services").then(({ data }) => setServices(data.services)).catch(() => {}); }, [load]);

  const addWindow = (day) => setWh((w) => ({ ...w, [day]: [...(w[day] || []), { start: "10:00", end: "13:00" }] }));
  const removeWindow = (day, i) => setWh((w) => ({ ...w, [day]: w[day].filter((_, idx) => idx !== i) }));
  const setWindow = (day, i, key, val) => setWh((w) => ({ ...w, [day]: w[day].map((x, idx) => idx === i ? { ...x, [key]: val } : x) }));

  const saveSchedule = async () => {
    setSaving(true);
    try {
      const cleaned = {};
      Object.entries(wh).forEach(([d, arr]) => { if (arr && arr.length) cleaned[d] = arr; });
      const { data } = await api.put(`/doctors/${id}`, { working_hours: cleaned, leaves });
      setDoctor(data); toast.success("Schedule saved");
    } catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  const addLeave = () => { if (newLeave && !leaves.includes(newLeave)) { setLeaves([...leaves, newLeave].sort()); setNewLeave(""); } };

  if (loading || !doctor) return <div className="space-y-6"><Skeleton className="h-8 w-48" /><Skeleton className="h-96" /></div>;

  return (
    <div className="animate-fade-up space-y-6" data-testid="doctor-detail-page">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <Button variant="outline" size="icon" onClick={() => navigate("/doctors")} data-testid="back-to-doctors"><ArrowLeft className="h-4 w-4" /></Button>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="font-heading text-2xl font-semibold tracking-tight text-slate-900">{doctor.name}</h1>
              <Badge className={doctor.is_active ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}>{doctor.is_active ? "Active" : "Inactive"}</Badge>
            </div>
            <p className="mt-1 text-sm text-slate-500">{doctor.specialization} · {doctor.department} · {doctor.slot_duration}m slots</p>
          </div>
        </div>
        <Button variant="outline" onClick={() => setEditOpen(true)} data-testid="doctor-edit"><Pencil className="h-4 w-4" /> Edit Profile</Button>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        <Card className="shadow-sm lg:col-span-2" data-testid="schedule-editor">
          <CardHeader className="flex-row items-center justify-between pb-2">
            <CardTitle className="font-heading text-base">Weekly Working Hours</CardTitle>
            <Button size="sm" onClick={saveSchedule} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="save-schedule">
              <Save className="h-4 w-4" /> Save
            </Button>
          </CardHeader>
          <CardContent className="space-y-3">
            {WEEKDAYS.map(({ key, label }) => (
              <div key={key} className="flex flex-col gap-2 rounded-lg border border-slate-100 p-3 sm:flex-row sm:items-center">
                <div className="flex w-28 items-center justify-between">
                  <span className="text-sm font-medium text-slate-700">{label}</span>
                  <Switch checked={(wh[key] || []).length > 0} onCheckedChange={(v) => v ? addWindow(key) : setWh((w) => ({ ...w, [key]: [] }))} data-testid={`day-toggle-${key}`} />
                </div>
                <div className="flex flex-1 flex-wrap gap-2">
                  {(wh[key] || []).length === 0 ? <span className="text-xs text-slate-400">Day off</span>
                    : (wh[key] || []).map((win, i) => (
                      <div key={i} className="flex items-center gap-1 rounded-md bg-slate-50 px-2 py-1">
                        <Input type="time" value={win.start} onChange={(e) => setWindow(key, i, "start", e.target.value)} className="h-7 w-24 text-xs" />
                        <span className="text-slate-400">–</span>
                        <Input type="time" value={win.end} onChange={(e) => setWindow(key, i, "end", e.target.value)} className="h-7 w-24 text-xs" />
                        <button onClick={() => removeWindow(key, i)} className="text-slate-400 hover:text-red-500"><X className="h-3.5 w-3.5" /></button>
                      </div>
                    ))}
                  {(wh[key] || []).length > 0 && <button onClick={() => addWindow(key)} className="flex items-center gap-1 rounded-md border border-dashed border-slate-300 px-2 py-1 text-xs text-slate-500 hover:border-emerald-300"><Plus className="h-3 w-3" /> Add</button>}
                </div>
              </div>
            ))}
          </CardContent>
        </Card>

        <div className="space-y-6">
          <Card className="shadow-sm">
            <CardHeader className="pb-2"><CardTitle className="font-heading text-base">Contact & Services</CardTitle></CardHeader>
            <CardContent className="space-y-2 text-sm">
              <p className="text-slate-600">Phone: {doctor.phone || "—"}</p>
              <p className="text-slate-600">Fee: {doctor.consultation_fee ? `₹${doctor.consultation_fee}` : "—"}</p>
              <div className="flex flex-wrap gap-1 pt-1">
                {(doctor.service_ids || []).map((sid) => {
                  const s = services.find((x) => x.id === sid);
                  return s ? <Badge key={sid} variant="secondary" className="bg-slate-100">{s.name}</Badge> : null;
                })}
              </div>
            </CardContent>
          </Card>

          <Card className="shadow-sm">
            <CardHeader className="pb-2"><CardTitle className="font-heading text-base flex items-center gap-2"><CalendarOff className="h-4 w-4" /> Leave / Unavailable</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <div className="flex gap-2">
                <Input type="date" value={newLeave} onChange={(e) => setNewLeave(e.target.value)} data-testid="leave-input" />
                <Button variant="outline" size="icon" onClick={addLeave} data-testid="add-leave"><Plus className="h-4 w-4" /></Button>
              </div>
              <div className="flex flex-wrap gap-2">
                {leaves.length === 0 ? <span className="text-xs text-slate-400">No leave dates.</span>
                  : leaves.map((d) => (
                    <Badge key={d} variant="secondary" className="gap-1 bg-red-50 text-red-600">{d}<button onClick={() => setLeaves(leaves.filter((x) => x !== d))}><X className="h-3 w-3" /></button></Badge>
                  ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      <DoctorFormDialog open={editOpen} onOpenChange={setEditOpen} doctor={doctor} services={services} onSaved={load} />
    </div>
  );
}
